// File: src/pages/candidate/TestResultPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import UpgradeRequestModal from '../../components/UpgradeRequestModal';

// --- ICONS SVG ---
const IconTrophy = () => (<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="rgba(252, 211, 77, 0.2)" stroke="#FCD34D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path></svg>);
const IconRobot = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg>;
const IconSend = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>;
const IconWarningBig = () => <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;

export default function TestResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Data State
  const [learningPath, setLearningPath] = useState(null);
  const [isLoadingPath, setIsLoadingPath] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  
  // Modal State
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [modalType, setModalType] = useState("");

  // Chatbot State
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  
  // ✅ FIX SCROLL: Dùng Ref cho container chat thay vì element cuối
  const chatContainerRef = useRef(null);

  const {
    score = 0,
    totalQuestions = 0,
    correctCount = 0,
    violationCount = 0,
    isTerminated = false,
    skillAnalysis = [],
    aiFeedback = "Chào bạn! Mình là AI Mentor. Dựa trên bài làm của bạn, mình thấy kết quả khá khả quan. Bạn có muốn hỏi thêm gì về các câu sai không?",
    aiSuggestion = "",
    testName = "Chuyên môn",
    testType = "mcq", // Nhận thêm testType
    questions = [],   // Nhận thêm questions
    userAnswers = {}
  } = location.state || {};
  const isManualGrading = ['code', 'essay', 'practical'].includes(testType);

  // ✅ FIX SCROLL: Luôn cuộn lên đầu trang khi mới vào
  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

  // Init Data & Check Premium
  useEffect(() => {
      setMessages([
          { role: 'model', content: aiFeedback + (aiSuggestion ? `\n\n💡 Gợi ý: ${aiSuggestion}` : "") }
      ]);

      const checkPlan = async () => {
          const token = localStorage.getItem('skillmatch_token');
          const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
          try {
              const res = await fetch(`${API_URL}/api/users/profile`, {
                  headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await res.json();
              setIsPremium(data.subscription?.plan === 'premium');
          } catch (e) {}
      };
      checkPlan();
  }, []);

  // ✅ FIX SCROLL: Chỉ cuộn nội dung TRONG khung chat
  useEffect(() => {
      if (chatContainerRef.current) {
          const { scrollHeight, clientHeight } = chatContainerRef.current;
          chatContainerRef.current.scrollTo({
              top: scrollHeight - clientHeight,
              behavior: 'smooth'
          });
      }
  }, [messages]);
  const getWeakSkills = () => {
      return skillAnalysis
          .filter(skill => skill.score < 7) // Coi điểm dưới 7 là yếu
          .map(skill => skill.label)
          .join(', ') || "Nâng cao kiến thức tổng quát";
  };

  // --- HANDLERS ---

  const handleGeneratePath = async () => {
    if (!isPremium) {
          setModalType("Lộ trình học tập (AI Mentor)");
          setShowUpgradeModal(true);
          return;
      }
    setIsLoadingPath(true);
    try {
      const token = localStorage.getItem('skillmatch_token');
      const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

      const res = await fetch(`${API_URL}/api/ai/learning-path`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ testName, 
            score, 
            totalQuestions, 
            skillAnalysis,
            // ✅ GỬI THÊM DỮ LIỆU ĐỂ AI ĐỌC HIỂU
            questions, 
            userAnswers })
      });
      const data = await res.json();
      setLearningPath(data);

      setTimeout(() => {
        const section = document.getElementById('learning-path-section');
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 100);

    } catch (err) {
      alert("Lỗi tạo lộ trình: " + err.message);
    } finally {
      setIsLoadingPath(false);
    }
  };

  const handleUnlockChart = () => {
      if (!isPremium) {
          setModalType("Biểu đồ phân tích chuyên sâu");
          setShowUpgradeModal(true);
      }
  };

  const handleChatSend = async () => {
    if (!isPremium) {
        setModalType("AI Mentor & Chat chuyên sâu");
        setShowUpgradeModal(true); // 🔥 KHÓA MENTOR
        return;
    }
      if (!chatInput.trim()) return;
      
      const userMsg = { role: 'user', content: chatInput };
      setMessages(prev => [...prev, userMsg]);
      setChatInput("");
      setIsChatLoading(true);

      try {
          const token = localStorage.getItem('skillmatch_token');
          const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
          
          const res = await fetch(`${API_URL}/api/ai/chat-feedback`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ 
                  messages: [...messages, userMsg],
                  testContext: { score, topic: testName,         // Lấy từ testName thực tế (Marketing,...)
                      weakSkills: getWeakSkills() } 
              })
          });
          
          const data = await res.json();
          setMessages(prev => [...prev, { role: 'model', content: data.reply || "Mình chưa hiểu ý bạn lắm, hãy hỏi chi tiết hơn nhé!" }]);

      } catch (err) {
          setMessages(prev => [...prev, { role: 'model', content: "⚠️ Xin lỗi, mình đang gặp sự cố kết nối. Thử lại sau nhé!" }]);
      } finally {
          setIsChatLoading(false);
      }
  };

  // --- RENDER HELPERS ---
  const percentage = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const getPercentile = (s) => s >= 95 ? 'Top 1%' : (s >= 80 ? 'Top 10%' : (s >= 50 ? 'Top 50%' : '---'));
  
  const generateRadarPoints = (data, scale = 1) => {
    const totalPoints = data.length;
    const radius = 90 * scale;
    const centerX = 125;
    const centerY = 110;
    return data.map((item, index) => {
      const value = (item.score / 10);
      const angle = (Math.PI * 2 * index) / totalPoints - Math.PI / 2;
      const x = centerX + (radius * value * Math.cos(angle));
      const y = centerY + (radius * value * Math.sin(angle));
      return `${x},${y}`;
    }).join(' ');
  };
  const gridLevels = [1, 0.75, 0.5, 0.25];
  const dataPoints = skillAnalysis.length > 0 ? generateRadarPoints(skillAnalysis) : "";
  const colorThemes = [{ bg: '#EFF6FF', bar: '#3B82F6', text: '#1E3A8A' }, { bg: '#FFFBEB', bar: '#F59E0B', text: '#78350F' }, { bg: '#ECFDF5', bar: '#10B981', text: '#064E3B' }, { bg: '#F5F3FF', bar: '#8B5CF6', text: '#4C1D95' }];

  if (isTerminated) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FEF2F2' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '16px', textAlign: 'center', maxWidth: '500px' }}>
            <IconWarningBig />
            <h1 style={{ color: '#B91C1C' }}>Bài thi bị hủy!</h1>
            <p>Phát hiện vi phạm {violationCount} lần.</p>
            <button onClick={() => navigate('/candidate/tests')}>Quay về</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F3F4F6', minHeight: '100vh', fontFamily: 'Inter, sans-serif', padding: '100px 20px 150px' }}>

      {/* 1. HEADER SCORE */}
     <div style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)', padding: '40px 20px', color: 'white', textAlign: 'center', borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.4)', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ width: '80px', height: '80px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', backdropFilter: 'blur(10px)' }}>
          {isManualGrading ? <span className="material-symbols-outlined" style={{ fontSize: 40 }}>hourglass_top</span> : <IconTrophy />}
        </div>
        
        {/* ✅ LOGIC HIỂN THỊ ĐIỂM */}
        {isManualGrading ? (
            <div>
                <h1 style={{ fontSize: '36px', fontWeight: '800', margin: '0 0 8px' }}>Đang chờ chấm điểm</h1>
                <div style={{ fontSize: '16px', fontWeight: '500', opacity: 0.9 }}>
                    Bài làm của bạn đã được gửi. AI sẽ phân tích chi tiết bên dưới.
                </div>
            </div>
        ) : (
            <>
                <h1 style={{ fontSize: '56px', fontWeight: '800', margin: '0 0 8px' }}>{score}/100</h1>
                <div style={{ fontSize: '20px', fontWeight: '600', opacity: 0.9 }}>Kết quả bài kiểm tra năng lực</div>
            </>
        )}
        
        {!isManualGrading && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '24px' }}>
              <div><div style={{ fontSize: '24px', fontWeight: '700' }}>{getPercentile(score)}</div><div style={{ fontSize: '12px', opacity: 0.8 }}>Xếp hạng</div></div>
              <div style={{ width: '1px', background: 'rgba(255,255,255,0.3)', height: '40px' }}></div>
              <div><div style={{ fontSize: '24px', fontWeight: '700' }}>{correctCount}/{totalQuestions}</div><div style={{ fontSize: '12px', opacity: 0.8 }}>Câu đúng</div></div>
            </div>
        )}
      </div>

      {/* 2. MAIN GRID */}
      <div style={{ maxWidth: '1100px', margin: '32px auto 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

        {/* --- LEFT COLUMN: SKILL CHART --- */}
        <div 
            style={{ 
                backgroundColor: 'white', 
                borderRadius: '24px', 
                padding: '32px', 
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden',
                marginBottom: '24px' // ✅ Fix dính lề
            }} 
            onClick={handleUnlockChart}
        >
          {!isPremium && (
              <div className="absolute inset-0 z-10 bg-white/70 backdrop-blur-sm flex flex-col items-center justify-center text-center cursor-pointer hover:bg-white/60 transition-colors">
                  <span className="material-symbols-outlined text-5xl text-yellow-500 mb-2">lock</span>
                  <h3 className="text-xl font-bold text-gray-800">Biểu đồ chuyên sâu</h3>
                  <p className="text-gray-500 text-sm mb-4">Dành riêng cho thành viên Premium</p>
                  <button className="px-6 py-2 bg-gray-900 text-white rounded-full font-bold text-sm shadow-lg hover:scale-105 transition-transform">Mở khóa ngay</button>
              </div>
          )}

          <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '24px' }}>Skill Performance</h3>
          
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
            <svg width="250" height="220" viewBox="0 0 250 220" style={{ overflow: 'visible' }}>
              {gridLevels.map((scale, i) => (<polygon key={i} points={generateRadarPoints(skillAnalysis.map(() => ({ score: 10 })), scale)} fill={i === 0 ? "#F9FAFB" : "none"} stroke="#E5E7EB" strokeWidth="1"/>))}
              {skillAnalysis.map((_, i) => {
                const angle = (Math.PI * 2 * i) / skillAnalysis.length - Math.PI / 2;
                return <line key={i} x1="125" y1="110" x2={125 + 90 * Math.cos(angle)} y2={110 + 90 * Math.sin(angle)} stroke="#E5E7EB" />;
              })}
              <polygon points={dataPoints} fill="rgba(59, 130, 246, 0.2)" stroke="#3B82F6" strokeWidth="2" strokeLinejoin="round" />
              {skillAnalysis.map((item, i) => {
                  const angle = (Math.PI * 2 * i) / skillAnalysis.length - Math.PI / 2;
                  const x = 125 + 110 * Math.cos(angle);
                  const y = 110 + 110 * Math.sin(angle);
                  return <text key={i} x={x} y={y} fontSize="11" fontWeight="600" fill="#4B5563" textAnchor="middle" dominantBaseline="middle">{item.label}</text>;
              })}
            </svg>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {skillAnalysis.map((item, i) => {
              const theme = colorThemes[i % colorThemes.length];
              return (
                <div key={i} style={{ backgroundColor: theme.bg, borderRadius: '12px', padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{item.label}</span>
                    <span style={{ fontSize: '14px', fontWeight: '800', color: theme.text }}>{item.score}</span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.5)', borderRadius: '4px' }}>
                    <div style={{ width: `${item.score * 10}%`, height: '100%', background: theme.bar, borderRadius: '4px' }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* --- RIGHT COLUMN: AI CHATBOT --- */}
        <div style={{ display: 'flex', flexDirection: 'column', height: '600px', position: 'relative' }}>
            {!isPremium && (
                <div style={{ position: 'absolute', inset: '5px 0 0 0', zIndex: 20, backgroundColor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(4px)', borderRadius: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '48px', color: '#F59E0B', marginBottom: '12px' }}>lock</span>
                    <h4 style={{ fontWeight: '700', color: '#1F2937' }}>AI Mentor </h4>
                    <p style={{ fontSize: '13px', color: '#6B7280', padding: '0 20px' }}>Nâng cấp Premium để hỏi đáp chuyên sâu về ngành **{testName}**.</p>
                    <button onClick={() => { setModalType("AI Mentor"); setShowUpgradeModal(true); }} style={{ marginTop: '16px', padding: '10px 24px', background: '#111827', color: 'white', borderRadius: '99px', border: 'none', fontWeight: '700', cursor: 'pointer' }}>Mở khóa ngay</button>
                </div>
            )}

            <div style={{ backgroundColor: 'white', borderRadius: '24px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ background: '#8B5CF6', padding: '8px', borderRadius: '10px' }}><IconRobot /></div>
                    <div>
                        <span style={{ fontWeight: '800', fontSize: '15px', color: '#1F2937', display: 'block' }}>AI Mentor </span>
                        <span style={{ fontSize: '11px', color: '#10B981', fontWeight: '600' }}>● Trực tuyến</span>
                    </div>
                </div>

                <div ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', paddingRight: '8px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {messages.map((msg, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                            <div style={{ maxWidth: '85%', padding: '12px 16px', borderRadius: '16px', backgroundColor: msg.role === 'user' ? '#3B82F6' : '#F3F4F6', color: msg.role === 'user' ? 'white' : '#1F2937', fontSize: '14px', lineHeight: '1.5', borderBottomRightRadius: msg.role === 'user' ? '4px' : '16px', borderBottomLeftRadius: msg.role === 'model' ? '4px' : '16px' }}>
                                {msg.content.split('\n').map((line, i) => <div key={i}>{line || <br/>}</div>)}
                            </div>
                        </div>
                    ))}
                    {isChatLoading && <div style={{ display: 'flex' }}><div style={{ backgroundColor: '#F3F4F6', padding: '12px', borderRadius: '16px', borderBottomLeftRadius: '4px' }}><span className="typing-dot"></span><span className="typing-dot"></span><span className="typing-dot"></span></div></div>}
                </div>

                <div style={{ marginTop: '16px', borderTop: '1px solid #E5E7EB', paddingTop: '16px', display: 'flex', gap: '10px' }}>
                    <input 
                        type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
                        placeholder={isPremium ? "Hỏi Mentor về các câu sai..." : "Vui lòng nâng cấp để chat..."} 
                        disabled={!isPremium || isChatLoading}
                        style={{ flex: 1, padding: '12px 16px', borderRadius: '12px', border: '1px solid #E5E7EB', outline: 'none', fontSize: '14px', backgroundColor: '#F9FAFB', color: '#111827' }} 
                    />
                    <button onClick={handleChatSend} disabled={!isPremium || !chatInput.trim() || isChatLoading} style={{ width: '44px', height: '44px', background: '#2563EB', borderRadius: '12px', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: isPremium && chatInput.trim() ? 1 : 0.5 }}><IconSend /></button>
                </div>
            </div>
        </div>

        {/* DETAILED BREAKDOWN */}
        <div style={{ gridColumn: 'span 2', backgroundColor: 'white', borderRadius: '24px', padding: '32px', marginTop: '8px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '20px' }}>Detailed Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
            <StatBox label="Questions Answered" val={totalQuestions} color="#111827" bg="#F9FAFB" />
            <StatBox label="Correct Answers" val={correctCount} color="#059669" bg="#ECFDF5" />
            <StatBox label="Incorrect Answers" val={totalQuestions - correctCount} color="#DC2626" bg="#FEF2F2" />
            <StatBox label="Accuracy Rate" val={`${percentage}%`} color="#2563EB" bg="#EFF6FF" />
          </div>
        </div>

        {/* CTA LEARNING PATH */}
        <div style={{ gridColumn: 'span 2', background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', borderRadius: '24px', padding: '40px', textAlign: 'center', color: 'white', marginTop: '24px', position: 'relative', overflow: 'hidden' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', marginBottom: '12px' }}>Ready to Level Up?</h2>
          <p style={{ opacity: 0.9, maxWidth: '600px', margin: '0 auto 24px' }}>Nhận lộ trình học tập cá nhân hóa được thiết kế riêng cho bạn.</p>
          <button onClick={handleGeneratePath} disabled={isLoadingPath} style={{ backgroundColor: 'white', color: '#4F46E5', padding: '14px 32px', borderRadius: '99px', border: 'none', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            {!isPremium ? <><span className="material-symbols-outlined">lock</span> Mở khóa Lộ Trình Học (Premium)</> : (isLoadingPath ? 'Đang tạo...' : '✨ Tạo Lộ Trình Học Tập')}
          </button>
        </div>

        {/* LEARNING PATH RESULT */}
        {learningPath && (
          <div id="learning-path-section" style={{ gridColumn: 'span 2', marginTop: '32px', backgroundColor: 'white', borderRadius: '24px', padding: '32px' }}>
            <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', marginBottom: '8px' }}>🚀 Lộ Trình Học Tập</h3>
            <p style={{ color: '#6B7280', marginBottom: '24px' }}>{learningPath.overview}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {learningPath.weeks?.map((week, idx) => (
                <div key={idx} style={{ backgroundColor: '#F9FAFB', borderRadius: '16px', padding: '20px', borderLeft: '4px solid #3B82F6' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#2563EB', marginBottom: '10px' }}>Tuần {week.week}: {week.focus}</h4>
                  <ul style={{ paddingLeft: '20px', fontSize: '14px', color: '#4B5563', lineHeight: '1.6' }}>{week.tasks?.map((t, i) => <li key={i}>{t}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
        )}
        {learningPath?.suggestedCourses && learningPath.suggestedCourses.length > 0 && (
  <div style={{ 
    gridColumn: 'span 2', 
    marginTop: '32px', 
    backgroundColor: 'white', 
    borderRadius: '24px', 
    padding: '32px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
      <div style={{ width: '40px', height: '40px', background: '#ECFDF5', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="material-symbols-outlined" style={{ color: '#10B981' }}>auto_stories</span>
      </div>
      <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#111827', margin: 0 }}>
        Khóa học MIỄN PHÍ gợi ý
      </h3>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
      {learningPath.suggestedCourses.map((course, idx) => (
        <div key={idx} style={{ 
          border: '1px solid #F3F4F6', 
          borderRadius: '20px', 
          padding: '24px', 
          backgroundColor: '#F9FAFB',
          transition: 'transform 0.2s',
          cursor: 'default'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <span style={{ 
              fontSize: '11px', 
              fontWeight: '700', 
              textTransform: 'uppercase', 
              color: '#3B82F6', 
              background: '#EFF6FF', 
              padding: '4px 12px', 
              borderRadius: '99px' 
            }}>
              {course.platform}
            </span>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#059669' }}>FREE 🎁</span>
          </div>
          
          <h4 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', marginBottom: '10px', lineHeight: '1.4' }}>
            {course.title}
          </h4>
          
          <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px', lineHeight: '1.5' }}>
            {course.reason}
          </p>
          
          <a 
            href={course.link} 
            target="_blank" 
            rel="noreferrer" 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px', 
              width: '100%', 
              padding: '12px', 
              background: '#111827', 
              color: 'white', 
              borderRadius: '12px', 
              textDecoration: 'none', 
              fontSize: '14px', 
              fontWeight: '600' 
            }}
          >
            Học miễn phí ngay <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>open_in_new</span>
          </a>
        </div>
      ))}
    </div>
  </div>
)}

      </div>

      <UpgradeRequestModal 
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            title={`Mở khóa ${modalType}`}
            message={`Tính năng "${modalType}" chỉ dành cho gói Premium. Nâng cấp để truy cập ngay!`}
            targetPlan="Premium"
        />
        
      <style>{`
        .typing-dot { display: inline-block; width: 6px; height: 6px; margin-right: 3px; background: #9CA3AF; border-radius: 50%; animation: typing 1.4s infinite ease-in-out both; }
        .typing-dot:nth-child(1) { animation-delay: -0.32s; }
        .typing-dot:nth-child(2) { animation-delay: -0.16s; }
        @keyframes typing { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
      `}</style>
    </div>
  );
}

const StatBox = ({ label, val, color, bg }) => (
  <div style={{ backgroundColor: bg, padding: '20px', borderRadius: '16px', textAlign: 'center' }}>
    <div style={{ fontSize: '24px', fontWeight: '800', color: color, marginBottom: '4px' }}>{val}</div>
    <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>{label}</div>
  </div>
);